import { useState } from "react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { chipToast } from "@/components/ui/chip-toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "./profile-form";

const INCLUDE_ITEMS = [
  msg`Profile details and account preferences`,
  msg`Security and sign-in history`,
  msg`Notification preferences`,
  msg`Personal activity related to your account`,
];

export function DownloadDataDialog({
  open,
  onOpenChange,
  email,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
}) {
  const { t } = useLingui();
  const [checked, setChecked] = useState<boolean[]>(() =>
    INCLUDE_ITEMS.map(() => true),
  );
  const allChecked = checked.every(Boolean);

  const setAll = (value: boolean) =>
    setChecked(INCLUDE_ITEMS.map(() => value));
  const toggleOne = (index: number) =>
    setChecked((prev) => prev.map((c, i) => (i === index ? !c : c)));

  const handleRequest = () => {
    // No export backend yet (deferred) — presentational confirmation.
    chipToast({
      message: t`Data export requested. A secure download link will be sent to ${email} when ready.`,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-175">
        <DialogHeader>
          <DialogTitle><Trans>Download my data</Trans></DialogTitle>
          <DialogDescription>
            <Trans>
              Request a downloadable copy of your profile, preferences, and
              account-related personal data.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <Field label={t`Send export link to`}>
            <div className="flex h-11 items-center rounded-lg border border-(--color-border) bg-(--color-surface-muted) px-3 text-sm text-(--color-text-primary)">
              {email}
            </div>
          </Field>

          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-(--color-text-primary)">
                <Trans>Include</Trans>
              </span>
              <label className="flex cursor-pointer items-center gap-2">
                <Switch checked={allChecked} onCheckedChange={setAll} />
                <span className="text-sm text-(--color-text-secondary)">
                  <Trans>Select all sections</Trans>
                </span>
              </label>
            </div>

            {INCLUDE_ITEMS.map((item, i) => (
              <label
                key={item.message}
                className="flex h-11 cursor-pointer items-center gap-3 rounded-lg border border-(--color-border) px-3"
              >
                <Checkbox
                  checked={checked[i]}
                  onCheckedChange={() => toggleOne(i)}
                />
                <span className="text-sm text-(--color-text-primary)">
                  {t(item)}
                </span>
              </label>
            ))}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button type="button" onClick={handleRequest}>
            <Trans>Request export</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
